<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import CampaignReportsAPI from 'dashboard/api/teletel/campaignReports';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { useAlert } from 'dashboard/composables';
import { downloadCsvFile } from 'dashboard/helper/downloadHelper';

const { t } = useI18n();

const isLoading = ref(true);
const hasError = ref(false);
const rows = ref([]);
const totals = ref(null);

const hasRows = computed(() => rows.value.length > 0);

const statusColumns = ['sent', 'delivered', 'read', 'failed', 'pending'];

const formatDate = value => {
  if (!value) return '—';
  return new Date(value).toLocaleDateString();
};

// Owner order 2026-09-14 (campaign audit, option 5): status per row and a
// CSV of the same table.
const statusLabel = row => {
  if (row.campaign_status === 'active' && row.enabled === false)
    return t('CAMPAIGN.WHATSAPP.CARD.STATUS.PAUSED');
  if (row.campaign_status === 'active')
    return t('CAMPAIGN.WHATSAPP.CARD.STATUS.SCHEDULED');
  if (row.campaign_status === 'completed' && row.unreached)
    return t('CAMPAIGN.WHATSAPP.CARD.STATUS.COMPLETED_WITH_SHORTFALL', {
      count: row.unreached,
    });
  return t(
    `CAMPAIGN.WHATSAPP.CARD.STATUS.${row.campaign_status.toUpperCase()}`
  );
};

const isExporting = ref(false);
const exportCsv = async () => {
  if (isExporting.value) return;
  isExporting.value = true;
  try {
    const response = await CampaignReportsAPI.export();
    downloadCsvFile('campaign-report.csv', response.data);
  } catch {
    useAlert(t('CAMPAIGN.REPORTS.EXPORT_ERROR'));
  } finally {
    isExporting.value = false;
  }
};

onMounted(async () => {
  try {
    const { data } = await CampaignReportsAPI.get();
    rows.value = data.payload;
    totals.value = data.totals;
  } catch {
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
});
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-auto p-6 gap-4">
    <div class="flex flex-wrap items-start justify-between gap-3">
      <div>
        <h1 class="text-xl font-semibold text-n-slate-12">
          {{ t('CAMPAIGN.REPORTS.TITLE') }}
        </h1>
        <p class="mt-1 text-sm text-n-slate-11">
          {{ t('CAMPAIGN.REPORTS.DESCRIPTION') }}
        </p>
      </div>
      <Button
        v-if="hasRows"
        :label="t('CAMPAIGN.REPORTS.EXPORT')"
        icon="i-lucide-download"
        size="sm"
        color="slate"
        variant="faded"
        :is-loading="isExporting"
        @click="exportCsv"
      />
    </div>

    <div v-if="isLoading" class="flex justify-center py-16">
      <Spinner />
    </div>

    <p v-else-if="hasError" class="text-sm text-n-ruby-11">
      {{ t('CAMPAIGN.REPORTS.ERROR') }}
    </p>

    <p v-else-if="!hasRows" class="text-sm text-n-slate-11">
      {{ t('CAMPAIGN.REPORTS.EMPTY') }}
    </p>

    <template v-else>
      <div
        v-if="totals"
        class="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-7"
      >
        <div class="rounded-lg border border-n-weak bg-n-solid-1 p-3">
          <p class="text-xs text-n-slate-11">
            {{ t('CAMPAIGN.REPORTS.TOTALS.PLANNED') }}
          </p>
          <p class="text-lg font-semibold text-n-slate-12">
            {{ totals.planned }}
          </p>
        </div>
        <div
          v-for="status in statusColumns"
          :key="status"
          class="rounded-lg border border-n-weak bg-n-solid-1 p-3"
        >
          <p class="text-xs text-n-slate-11">
            {{ t(`CAMPAIGN.REPORTS.STATUSES.${status.toUpperCase()}`) }}
          </p>
          <p class="text-lg font-semibold text-n-slate-12">
            {{ totals.statuses[status] }}
          </p>
        </div>
        <div class="rounded-lg border border-n-weak bg-n-solid-1 p-3">
          <p class="text-xs text-n-slate-11">
            {{ t('CAMPAIGN.REPORTS.TOTALS.DELIVERY_RATE') }}
          </p>
          <p class="text-lg font-semibold text-n-slate-12">
            {{ totals.delivery_rate }}%
          </p>
        </div>
      </div>

      <div class="overflow-x-auto rounded-lg border border-n-weak">
        <table class="w-full text-sm">
          <thead>
            <tr class="border-b border-n-weak bg-n-solid-1 text-start">
              <th class="p-3 text-start">
                {{ t('CAMPAIGN.REPORTS.COLUMNS.CAMPAIGN') }}
              </th>
              <th class="p-3 text-start">
                {{ t('CAMPAIGN.REPORTS.COLUMNS.INBOX') }}
              </th>
              <th class="p-3 text-start">
                {{ t('CAMPAIGN.REPORTS.COLUMNS.STATUS') }}
              </th>
              <th class="p-3 text-start">
                {{ t('CAMPAIGN.REPORTS.COLUMNS.DATE') }}
              </th>
              <th class="p-3 text-center">
                {{ t('CAMPAIGN.REPORTS.TOTALS.PLANNED') }}
              </th>
              <th
                v-for="status in statusColumns"
                :key="status"
                class="p-3 text-center"
              >
                {{ t(`CAMPAIGN.REPORTS.STATUSES.${status.toUpperCase()}`) }}
              </th>
              <th class="p-3 text-center">
                {{ t('CAMPAIGN.REPORTS.TOTALS.DELIVERY_RATE') }}
              </th>
              <th class="p-3 text-center">
                {{ t('CAMPAIGN.REPORTS.TOTALS.READ_RATE') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in rows"
              :key="row.id"
              class="border-b border-n-weak last:border-b-0"
            >
              <td class="p-3 font-medium text-n-slate-12">{{ row.title }}</td>
              <td class="p-3 text-n-slate-11">{{ row.inbox_name || '—' }}</td>
              <td class="p-3 text-n-slate-11">{{ statusLabel(row) }}</td>
              <td class="p-3 text-n-slate-11">
                {{ formatDate(row.scheduled_at) }}
              </td>
              <td class="p-3 text-center">{{ row.planned }}</td>
              <td
                v-for="status in statusColumns"
                :key="status"
                class="p-3 text-center"
              >
                {{ row.statuses[status] }}
              </td>
              <td class="p-3 text-center">{{ row.delivery_rate }}%</td>
              <td class="p-3 text-center">{{ row.read_rate }}%</td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
  </div>
</template>
